import { useState, useMemo } from 'react'
import { useLibraryStore } from '@/stores/library-store'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Loader2 } from 'lucide-react'
import { formatFileSize } from '@/lib/schemas'

interface DeleteDocumentsDialogProps {
  documentIds: string[]
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function DeleteDocumentsDialog({
  documentIds,
  open,
  onOpenChange,
}: DeleteDocumentsDialogProps) {
  const { documents, bulkDeleteDocuments } = useLibraryStore()
  const [isDeleting, setIsDeleting] = useState(false)

  // Get document details for the ones being deleted
  const documentsToDelete = useMemo(() => {
    const idSet = new Set(documentIds)
    return documents.filter((doc) => idSet.has(doc.id))
  }, [documents, documentIds])

  // Calculate total size
  const totalSize = useMemo(() => {
    return documentsToDelete.reduce((sum, doc) => sum + (doc.size || 0), 0)
  }, [documentsToDelete])

  const count = documentsToDelete.length

  const handleDelete = async () => {
    if (documentIds.length === 0) return

    setIsDeleting(true)
    try {
      await bulkDeleteDocuments(documentIds)
      onOpenChange(false)
    } catch {
      // Error handled in store
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Delete {count === 1 ? 'Document' : 'Documents'}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2">
              <p>
                Are you sure you want to delete{' '}
                <strong>
                  {count === 1 ? 'this document' : `${count} documents`}
                </strong>
                ?
              </p>
              {totalSize > 0 && (
                <p className="text-sm">
                  This will remove {formatFileSize(totalSize)} of data.
                </p>
              )}
              <p className="text-sm text-destructive">
                This action cannot be undone.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault()
              void handleDelete()
            }}
            disabled={isDeleting || count === 0}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Delete {count === 1 ? 'Document' : `${count} Documents`}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
